import React from 'react';
import { Box, Typography, Container, Paper, Button, Divider, Breadcrumbs, Link } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useLocation } from 'react-router-dom';
import { useThemeContext } from './context/ThemeContext';
import { BrandLogo } from './components/BrandLogo';

const POLICY_CONTENT = {
    terms: {
        title: "Terms of Service",
        lastUpdated: "April 7, 2026",
        sections: [
            {
                heading: "1. Acceptance of Terms",
                text: "By accessing or using the FlowState platform, you agree to be bound by these Terms of Service. If you do not agree, you may not use our services."
            },
            {
                heading: "2. Intellectual Property",
                text: "All content, including but not limited to interactive animations, curriculum data, code visualizations, and branding, is the sole property of FlowState. You are granted a limited, personal, non-exclusive license to view the content for educational purposes. Reproduction, distribution, or unauthorized commercial use is strictly prohibited."
            },
            {
                heading: "3. User Accounts",
                text: "You are responsible for maintaining the confidentiality of your account credentials. Any activity performed under your account is your responsibility. We reserve the right to terminate accounts that violate our community guidelines."
            },
            {
                heading: "4. Limitation of Liability",
                text: "FlowState provides educational content 'as is'. While we strive for 100% accuracy in our visualizations, we are not liable for any career outcomes, interview results, or technical decisions made based on our content."
            }
        ]
    },
    privacy: {
        title: "Privacy Policy",
        lastUpdated: "April 7, 2026",
        sections: [
            {
                heading: "1. Information We Collect",
                text: "We collect basic account information (name, email) and phone numbers specifically for callback requests and tutoring coordination. We do not sell your personal data to third parties."
            },
            {
                heading: "2. WhatsApp Integration",
                text: "When you request a callback, we use standard WhatsApp redirection protocols. Your phone number is only used to facilitate direct communication with our tutoring team."
            },
            {
                heading: "3. Cookies and Tracking",
                text: "We use essential cookies to manage your login sessions and anonymous analytics to improve our visualization engine's performance."
            },
            {
                heading: "4. Data Security",
                text: "Your payment information is processed entirely by secure, PCI-compliant third-party providers (e.g., Razorpay). FlowState never stores your credit card or banking details."
            }
        ]
    },
    refund: {
        title: "Refund & Cancellation Policy",
        lastUpdated: "April 7, 2026",
        sections: [
            {
                heading: "1. Digital Content Access",
                text: "Due to the nature of digital goods, once a course module or track has been purchased and access has been granted, we generally do not offer refunds."
            },
            {
                heading: "2. Exceptional Circumstances",
                text: "Refund requests made within 24 hours of purchase may be considered if the user has not accessed more than 10% of the lessons within the module. Decisions are made at the sole discretion of the FlowState support team."
            },
            {
                heading: "3. Tutoring Cancellations",
                text: "For 1:1 tutoring sessions, cancellations must be made 24 hours in advance to be eligible for rescheduling. Missed sessions without prior notice are non-refundable."
            }
        ]
    }
};

export const LegalPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { mode } = useThemeContext();
    const isLight = mode === 'light';

    // Determine which policy to show based on URL
    const pathname = location.pathname.split('/').pop();
    const policyKey = (pathname === 'terms' || pathname === 'privacy' || pathname === 'refund') ? pathname : 'terms';
    const content = POLICY_CONTENT[policyKey];

    return (
        <Box sx={{
            minHeight: '100vh',
            width: '100vw',
            bgcolor: isLight ? '#f8fafc' : '#0a0a0c',
            pb: 10
        }}>
            {/* Header / Nav */}
            <Box sx={{
                borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid #1f1f22',
                bgcolor: isLight ? '#fff' : '#000',
                py: 2,
                px: { xs: 3, md: 6 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box onClick={() => navigate('/interview-prep/')} sx={{ cursor: 'pointer' }}>
                    <BrandLogo size="small" isLight={isLight} />
                </Box>
                <Button
                    onClick={() => navigate('/interview-prep/')}
                    startIcon={<ArrowBackIcon />}
                    sx={{ textTransform: 'none', fontWeight: 700, color: isLight ? '#1e293b' : '#f1f5f9' }}
                >
                    Back to Platform
                </Button>
            </Box>

            <Container maxWidth="md" sx={{ mt: 8 }}>
                <Breadcrumbs sx={{ mb: 4, color: isLight ? '#64748b' : '#94a3b8' }}>
                    <Link underline="hover" color="inherit" onClick={() => navigate('/interview-prep/')} sx={{ cursor: 'pointer' }}>
                        FlowState
                    </Link>
                    <Typography color="text.primary" sx={{ fontWeight: 600, color: isLight ? '#1e293b' : '#f1f5f9' }}>{content.title}</Typography>
                </Breadcrumbs>

                <Paper elevation={0} sx={{
                    p: { xs: 4, md: 8 },
                    borderRadius: 4,
                    background: isLight ? '#ffffff' : 'rgba(255, 255, 255, 0.02)',
                    border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255, 255, 255, 0.05)',
                }}>
                    <Typography variant="h3" fontWeight="800" sx={{ mb: 1, color: isLight ? '#0f172a' : '#f1f5f9' }}>
                        {content.title}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 6, color: isLight ? '#64748b' : '#94a3b8', fontWeight: 500 }}>
                        Last Updated: {content.lastUpdated}
                    </Typography>

                    <Divider sx={{ mb: 6, opacity: 0.5 }} />

                    {content.sections.map((section, idx) => (
                        <Box key={idx} sx={{ mb: 5 }}>
                            <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: isLight ? '#1e293b' : '#f1f5f9' }}>
                                {section.heading}
                            </Typography>
                            <Typography variant="body1" sx={{ color: isLight ? '#475569' : '#cbd5e1', lineHeight: 1.8 }}>
                                {section.text}
                            </Typography>
                        </Box>
                    ))}
                    
                    <Box sx={{ mt: 10, p: 4, borderRadius: 3, bgcolor: isLight ? '#f1f5f9' : 'rgba(59, 130, 246, 0.05)', textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8', fontWeight: 600 }}>
                            Have questions about our {content.title.toLowerCase()}? 
                        </Typography>
                        <Button 
                            variant="text" 
                            onClick={() => navigate('/interview-prep/mentorship-inquiry')}
                            sx={{ fontWeight: 800, mt: 1 }}
                        >
                            Contact Support
                        </Button>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};
